import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { MessageSquare, Zap, SmilePlus, Globe, History as HistoryIcon, ChevronRight, ChevronLeft, Circle } from "lucide-react";
import { apiBaseUrl } from "../../../../services/config";
import TranscriptPanel from "./panels/TranscriptPanel";
import LastMeetingOverview from "./panels/LastMeetingOverview";
import EmotionsPanel from "./panels/EmotionsPanel";
import CaptionsPanel from "./panels/CaptionsPanel";
import HistoryPanel from "./panels/HistoryPanel";

const API = apiBaseUrl;
const authHeader = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("platform_token") || localStorage.getItem("platform_token") || ""}`,
});
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition || null;
const NEGATIVE_EMOTIONS = new Set(["confused", "frustrated", "disengaged"]);

const wsUrl = (meetingId) => {
  const proto = API.startsWith("https") ? "wss" : "ws";
  const base = API.replace(/^https?:\/\//, "");
  return `${proto}://${base}/hirality-meetings/ws/${meetingId}`;
};

const ALL_TOOLS = [
  { key: "transcript", icon: MessageSquare, label: "Transcript & Notes" },
  { key: "catchup",    icon: Zap,           label: "Last Meeting", recurringOnly: true },
  { key: "emotions",   icon: SmilePlus,     label: "Live Emotions" },
  { key: "captions",   icon: Globe,         label: "Live Captions" },
  { key: "history",    icon: HistoryIcon,   label: "Previous Sessions", recurringOnly: true },
];

// Transcript panel title per meeting_type (see meetingTypes.js on the
// list/detail pages) — purely cosmetic, same transcript/notes tool underneath.
const TRANSCRIPT_TITLE = {
  meeting: "Transcript & Notes",
  interview: "Candidate Notes",
  healthcare: "Patient Notes",
  ekyc: "Verification Notes",
};

// The live-call sidebar — an icon dock of independent tools rather than one
// single collapsible chat panel. Each tool is its own drawer (see ./panels);
// this shell owns the one websocket connection + speech recognition every
// tool reads from, and the ambient "something needs your attention" badges
// so a business user gets the required info live without opening every panel.
export default function LiveTranscriptPanel({ meetingId, displayName, meetingType = "meeting" }) {
  const [open, setOpen] = useState(true);
  const [activeTool, setActiveTool] = useState("transcript");
  const [lines, setLines] = useState([]);
  const [emotions, setEmotions] = useState({});
  const [connected, setConnected] = useState(false);
  const [unseenLines, setUnseenLines] = useState(0);
  const [emotionAlert, setEmotionAlert] = useState(false);
  // Last Meeting Overview renders as a left-anchored popup over the video,
  // not inside the right-side drawer — auto-opens once when the call
  // starts (if there's a previous occurrence to show), closeable, never
  // covers the call since it sits opposite the tool dock.
  const [overviewOpen, setOverviewOpen] = useState(false);
  const autoOpenedOverviewRef = useRef(false);

  const [languages, setLanguages] = useState([]);
  const [captionLang, setCaptionLang] = useState("");
  const [captions, setCaptions] = useState({});

  // Series history — only fetched to answer "is this meeting recurring?";
  // the History dock icon is hidden entirely for a one-off meeting.
  const [isRecurring, setIsRecurring] = useState(false);
  const [seriesLoading, setSeriesLoading] = useState(true);
  const [seriesOccurrences, setSeriesOccurrences] = useState([]);

  const wsRef = useRef(null);
  const recognitionRef = useRef(null);
  const captionLangRef = useRef("");
  const activeToolRef = useRef(activeTool);
  const openRef = useRef(open);

  useEffect(() => { activeToolRef.current = activeTool; }, [activeTool]);
  useEffect(() => { openRef.current = open; }, [open]);

  const send = useCallback((payload) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(payload));
    }
  }, []);

  // ── WebSocket: live relay of transcript, emotion reads, and captions ──
  useEffect(() => {
    if (!meetingId) return;
    let cancelled = false;
    let ws;

    const connect = () => {
      if (cancelled) return;
      ws = new WebSocket(wsUrl(meetingId));
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        // Reconnects drop the server's per-connection language selection.
        if (captionLangRef.current) {
          ws.send(JSON.stringify({ type: "set_caption_language", language: captionLangRef.current }));
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (!cancelled) setTimeout(connect, 2000);
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "transcript") {
            setLines((prev) => [...prev, msg]);
            if (!(openRef.current && activeToolRef.current === "transcript")) {
              setUnseenLines((n) => n + 1);
            }
          } else if (msg.type === "emotion") {
            setEmotions((prev) => ({ ...prev, ...msg.results }));
            const hasNegative = Object.values(msg.results).some((r) => NEGATIVE_EMOTIONS.has(r.emotion));
            if (hasNegative && !(openRef.current && activeToolRef.current === "emotions")) {
              setEmotionAlert(true);
            }
          } else if (msg.type === "caption") {
            setCaptions((prev) => ({ ...prev, [msg.timestamp]: { language: msg.language, text: msg.text } }));
          }
        } catch (e) { /* ignore malformed frame */ }
      };
    };
    connect();

    return () => {
      cancelled = true;
      wsRef.current?.close();
    };
  }, [meetingId]);

  // Fetch the supported caption/MOM language list once.
  useEffect(() => {
    axios.get(`${API}/hirality-meetings/languages`, { headers: authHeader() })
      .then((r) => setLanguages(r.data || []))
      .catch((e) => console.warn("Failed to load languages:", e.message));
  }, []);

  // Fetch this meeting's series history once — determines whether the
  // History tool even appears, and feeds it if it does.
  useEffect(() => {
    if (!meetingId) return;
    axios.get(`${API}/hirality-meetings/${meetingId}/series-history`, { headers: authHeader() })
      .then((r) => {
        setIsRecurring(!!r.data.is_recurring);
        setSeriesOccurrences(r.data.occurrences || []);
      })
      .catch((e) => console.warn("Failed to load series history:", e.message))
      .finally(() => setSeriesLoading(false));
  }, [meetingId]);

  // Auto-open the Last Meeting Overview once, as soon as we know whether
  // there's a previous occurrence — this is what makes it show automatically
  // when the call starts, not just on click.
  useEffect(() => {
    if (seriesLoading || autoOpenedOverviewRef.current) return;
    autoOpenedOverviewRef.current = true;
    if (seriesOccurrences.length > 0) setOverviewOpen(true);
  }, [seriesLoading, seriesOccurrences]);

  // Tell the server which language (if any) this participant wants captions in.
  useEffect(() => {
    captionLangRef.current = captionLang;
    send({ type: "set_caption_language", language: captionLang || null });
  }, [captionLang, send]);

  // ── Local speech recognition: this participant's own mic → WS ──
  useEffect(() => {
    if (!SpeechRecognition) return;
    const rec = new SpeechRecognition();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = "en-US";

    rec.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          const text = event.results[i][0].transcript.trim();
          if (text) send({ type: "transcript", speaker: displayName, text });
        }
      }
    };
    rec.onerror = (e) => {
      if (["no-speech", "audio-capture", "network"].includes(e.error)) {
        setTimeout(() => { try { rec.start(); } catch (_) {} }, 1500);
      }
    };
    rec.onend = () => { try { rec.start(); } catch (_) {} };

    try { rec.start(); recognitionRef.current = rec; } catch (_) {}
    return () => { try { recognitionRef.current?.stop(); } catch (_) {} };
  }, [displayName, send]);

  const tools = ALL_TOOLS.filter((t) => !t.recurringOnly || isRecurring);

  const selectTool = (key) => {
    if (key === "catchup") { setOverviewOpen((prev) => !prev); return; }
    setActiveTool((prev) => (prev === key ? null : key));
    if (key === "transcript") setUnseenLines(0);
    if (key === "emotions") setEmotionAlert(false);
  };

  return (
    <>
      <style>{`
        @keyframes hmSlideIn { from { transform: translateX(24px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes hmFadeUp  { from { transform: translateY(6px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes hmPulse   { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
        .hm-line { animation: hmFadeUp 0.25s ease both; }
        .hm-badge { transition: background 0.4s ease, color 0.4s ease, transform 0.2s ease; }
        .hm-scroll::-webkit-scrollbar { width: 6px; }
        .hm-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 4px; }
        .hm-spin { animation: hmSpin 0.8s linear infinite; }
        @keyframes hmSpin { to { transform: rotate(360deg); } }
        .hm-dock-btn:hover { background: rgba(255,255,255,0.09) !important; }
      `}</style>

      {/* Collapsed reopen tab */}
      {!open && (
        <button onClick={() => setOpen(true)} title="Show meeting tools"
          style={{ position: "fixed", top: "50%", right: 0, transform: "translateY(-50%)", zIndex: 1000,
            background: "linear-gradient(135deg,#00C8FF,#7C5CFF)", border: "none", borderRadius: "10px 0 0 10px",
            padding: "14px 8px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
            boxShadow: "-4px 0 20px rgba(0,0,0,0.3)" }}>
          <MessageSquare size={16} color="#fff" />
          <ChevronLeft size={14} color="#fff" />
          {(unseenLines > 0 || emotionAlert) && (
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#FF4D6D", position: "absolute", top: 6, right: 6 }} />
          )}
        </button>
      )}

      {open && (
        <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 999, display: "flex", fontFamily: "Outfit,sans-serif" }}>
          {/* Drawer — shows whichever tool is active */}
          {activeTool && (
            <div style={{ width: 340, background: "rgba(10,20,35,0.92)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
              borderLeft: "1px solid rgba(0,200,255,0.15)", display: "flex", flexDirection: "column", animation: "hmSlideIn 0.25s ease both" }}>
              {activeTool === "transcript" && (
                <TranscriptPanel lines={lines} connected={connected} onClose={() => setActiveTool(null)}
                  title={TRANSCRIPT_TITLE[meetingType] || TRANSCRIPT_TITLE.meeting} />
              )}
              {activeTool === "emotions"   && <EmotionsPanel emotions={emotions} onClose={() => setActiveTool(null)} />}
              {activeTool === "captions"   && (
                <CaptionsPanel languages={languages} captionLang={captionLang} setCaptionLang={setCaptionLang}
                  lines={lines} captions={captions} onClose={() => setActiveTool(null)} />
              )}
              {activeTool === "history"    && (
                <HistoryPanel loading={seriesLoading} occurrences={seriesOccurrences} onClose={() => setActiveTool(null)} />
              )}
            </div>
          )}

          {/* Icon dock — always visible while the sidebar is open */}
          <div style={{ width: 56, background: "rgba(10,20,35,0.96)", borderLeft: "1px solid rgba(255,255,255,0.08)",
            display: "flex", flexDirection: "column", alignItems: "center", padding: "14px 0", gap: 10 }}>
            <Circle size={8} fill={connected ? "#00E5A3" : "#FF4D6D"} color={connected ? "#00E5A3" : "#FF4D6D"}
              style={{ animation: connected ? "hmPulse 1.6s infinite" : "none", marginBottom: 4 }} title={connected ? "Connected" : "Reconnecting…"} />

            {tools.map(({ key, icon: Icon, label }) => {
              const isActive = key === "catchup" ? overviewOpen : activeTool === key;
              const badge = key === "transcript" ? (unseenLines > 0 ? (unseenLines > 9 ? "9+" : unseenLines) : null)
                : key === "emotions" ? (emotionAlert ? "!" : null)
                : key === "captions" ? (captionLang ? captionLang.toUpperCase() : null)
                : null;
              return (
                <button key={key} title={label} onClick={() => selectTool(key)} className="hm-dock-btn"
                  style={{ position: "relative", width: 40, height: 40, borderRadius: 11, border: "none", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: isActive ? "linear-gradient(135deg,#00C8FF,#7C5CFF)" : "rgba(255,255,255,0.05)",
                    transition: "background 0.2s" }}>
                  <Icon size={17} color={isActive ? "#fff" : "#7A93B4"} />
                  {badge != null && (
                    <span style={{ position: "absolute", top: -4, right: -4, minWidth: 16, height: 16, padding: "0 3px",
                      borderRadius: 8, background: key === "emotions" ? "#FF4D6D" : "#00C8FF", color: "#fff",
                      fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center",
                      border: "2px solid rgba(10,20,35,1)" }}>
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}

            <div style={{ flex: 1 }} />
            <button onClick={() => setOpen(false)} title="Hide" style={{ background: "none", border: "none", cursor: "pointer", color: "#7A93B4" }}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {overviewOpen && (
        <LastMeetingOverview occurrence={seriesOccurrences[0] || null} onClose={() => setOverviewOpen(false)} />
      )}
    </>
  );
}
